/* eslint-disable dot-notation */
/* eslint-disable complexity */
import { useContext, useEffect, useState } from 'react';
import sizeQuizUtilsDefaultCall, { addCustomEventListener, createSizeRecObject, isAnchorDetailsObjectInvalid } from '../../utils/sizeQuizUtils';
import GlobalContext from '../../contexts/GlobalContext';
import _ from 'lodash';
import { AllProductsInfo, SizeQuizData, SizeQuizGlobalConstants } from '../../types/sizeQuiz';
import { BotProcessor } from '../../utils/caf/botProcessor';
import { CAFContext } from '../../contexts/caf';
import { CAF_INITIALIZATION_SRC, CATEGORIES_INITIAL_STATE, CATEGORY_NAMES_MAPPING, RULE_NAMES_MAPPING } from '../../config/caf';
import {
  CAFContextValue,
  CAFModalOptions,
  ContextPool,
  ConversationObj,
  DispatchActionBasedContextOptions,
  HookInfo,
  HookOptions,
  ICAFState,
  ImageOptions,
  OptionClickInfo,
  SizeBrandObj
} from '../../types/caf';
import useAnchorPreferences from './sizeAdvisor';

const IMAGE_LINKS = {
  dress: 'DressesIcon',
  top: 'TopsIcon',
  pant: 'BottomsIcon'
};

export const useSKUTypeVsBrandVsSizeMap = (options: HookOptions): HookInfo => {
  const { globalContext } = useContext(GlobalContext);
  const [categoriesInfo, setCategoriesInfo] = useState({ ...CATEGORIES_INITIAL_STATE });
  const extractSizeChartDataFromStore = (): AllProductsInfo => {
    let sizeChartDataFromStore = globalContext.gb.utils.getParsedDataFromStore('allProductsInfo');

    if (_.isEmpty(sizeChartDataFromStore)) {
      sizeChartDataFromStore = {};
    }

    return sizeChartDataFromStore;
  };

  useEffect(() => {
    if (options.enabled) {
      setCategoriesInfo((prevHookInfo) => ({
        ...prevHookInfo, isLoading: true, isError: false, sizeQuizInfo: null
      }));
      addCustomEventListener(document, 'sizeQuizDataFetched', function () {
        const sizeChartDataFromStore = extractSizeChartDataFromStore();

        if (!_.isEmpty(sizeChartDataFromStore)) {
          setCategoriesInfo((prevHookInfo) => ({
            ...prevHookInfo,
            isFetched: true,
            isLoading: false,
            sizeQuizInfo: { ...sizeChartDataFromStore }
          }));
        } else {
          setCategoriesInfo((prevHookInfo) => ({
            ...prevHookInfo,
            isLoading: false,
            isFetched: false,
            sizeQuizInfo: null,
            isError: true
          }));
        }
      });
      sizeQuizUtilsDefaultCall(globalContext.gb);
    }

    return (): void => {
      document.removeEventListener('sizeQuizDataFetched', null);
    };
  }, [options.enabled]);

  return { ...categoriesInfo };
};

export const useCAFModal = (options: { initialState: ICAFState }) => {
  const { globalContext } = useContext(GlobalContext);
  const uuid = globalContext.gb.customer && globalContext.gb.customer.uuid;
  const isLoggedIn = !!(!_.isNull(uuid) && !_.isEmpty(uuid));
  const [cafState, setCAFState] = useState<ICAFState>({ ...options.initialState });
  const { cafContext } = useContext<CAFContextValue>(CAFContext);
  const {
    updateSizeQuizStatusAndAncorPReferances
  } = useAnchorPreferences();
  const createSKUCategories = (
    sizeQuizInfo: AllProductsInfo
  ): ImageOptions[] => Object.keys(sizeQuizInfo).map((item) => ({
    label: item,
    imageLink: IMAGE_LINKS[item.toLowerCase()]
  }));

  useEffect(() => {
    if (cafContext.sizeQuizInfo) {
      const totalSKUCategories = createSKUCategories(cafContext.sizeQuizInfo);

      setCAFState((_prevState) => ({ ..._prevState, remainingSKUCategories: totalSKUCategories }));
    }
  }, [cafContext.sizeQuizInfo]);

  const fetchConversationFromAction = ({ userIntent, contextPool }): ConversationObj => {
    const { ruleExecutionContext } = contextPool;
    const conversationByIntent = BotProcessor.processIntents({
      context: ruleExecutionContext, intent: userIntent, globalContext: globalContext
    });

    return conversationByIntent;
  };
  /**
   *
   * @param param0 action is next action which need to be performed
   * we should get the dependent variable of next action
   * if action was choose sku category first time, it means it has rendered,
   * we should create the next node(brand related stuff) and return the context related to it
   * @returns
   */
  const getDispatchActionBasedContext = (options: DispatchActionBasedContextOptions): ContextPool => {
    const { action: nextAction, choosedOptionInfo, contextMetaInfo } = options;
    let choosedSKUCategory;
    let choosedBrand;
    let choosedOccasionCategory;
    let remainingSKUCategories;
    let json = {};
    let sizeInfo = {};
    let sizeArr = null;
    let brandArr = null;
    let remainingSKUCategoriesArray = null;
    let selectedInfo = Object.assign({}, cafState.allSelectedInfo);

    switch (nextAction) {
      case RULE_NAMES_MAPPING.RULE_2:
        choosedOccasionCategory = choosedOptionInfo.label;
        remainingSKUCategories = contextMetaInfo.remainingSKUCategories;
        remainingSKUCategoriesArray = remainingSKUCategories.map((item) => ({ value: CATEGORY_NAMES_MAPPING[item.label], label: item.label, imageLink: item.imageLink }));

        return {
          ruleExecutionContext: { value: remainingSKUCategoriesArray },
          updatedInfo: { remainingSKUCategories: remainingSKUCategories, choosedOccasionCategory: choosedOccasionCategory }
        };

      case RULE_NAMES_MAPPING.RULE_3:
        choosedSKUCategory = choosedOptionInfo.label;
        remainingSKUCategories = contextMetaInfo.remainingSKUCategories.filter((item) => item.label.toLowerCase() !== choosedOptionInfo.label.toLowerCase());
        brandArr = Object.keys(cafContext.sizeQuizInfo[choosedSKUCategory]);
        json = brandArr && brandArr.length && brandArr.map((item) => ({ label: item, key: item }));

        return {
          ruleExecutionContext: { value: json as SizeBrandObj[], choosedSKUCategory: CATEGORY_NAMES_MAPPING[choosedSKUCategory].toLowerCase() },
          updatedInfo: { remainingSKUCategories: remainingSKUCategories, choosedSKUCategory: choosedSKUCategory }
        };

      case RULE_NAMES_MAPPING.RULE_4:
        choosedSKUCategory = contextMetaInfo.choosedSKUCategory;
        choosedBrand = choosedOptionInfo.label;
        sizeArr = Object.keys(cafContext.sizeQuizInfo[choosedSKUCategory][choosedBrand].sizes);
        json = sizeArr && sizeArr.length && sizeArr.map((item) => ({ label: item.replace(/_/g, '-'), key: item }));

        return {
          ruleExecutionContext: { value: json as SizeBrandObj[], choosedSKUCategory: CATEGORY_NAMES_MAPPING[choosedSKUCategory].toLowerCase(), choosedBrand: choosedBrand },
          updatedInfo: { choosedBrand: choosedBrand }
        };

      case RULE_NAMES_MAPPING.RULE_5:
        remainingSKUCategories = contextMetaInfo.remainingSKUCategories;
        remainingSKUCategoriesArray = remainingSKUCategories.map((item) => ({ value: CATEGORY_NAMES_MAPPING[item.label], label: item.label, imageLink: item.imageLink }));
        choosedSKUCategory = contextMetaInfo.choosedSKUCategory;
        choosedBrand = contextMetaInfo.choosedBrand;
        sizeInfo = cafContext.sizeQuizInfo[choosedSKUCategory][choosedBrand].sizes[choosedOptionInfo.label];
        selectedInfo = {
          ...selectedInfo,
          [choosedSKUCategory]: {
            anchorSizeBrand: {
              anchorBrand: choosedBrand,
              anchorSize: choosedOptionInfo.label,
              anchorType: sizeInfo['type'],
              anchorSubVendor: sizeInfo['subVendor'],
              productType: choosedSKUCategory
            }
          }
        };

        globalContext.gb.utils.saveToStore(SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES, JSON.stringify(selectedInfo));

        return {
          ruleExecutionContext: { value: remainingSKUCategoriesArray },
          updatedInfo: { allSelectedInfo: selectedInfo }
        };

      case RULE_NAMES_MAPPING.RULE_6:
        choosedSKUCategory = choosedOptionInfo.label;
        remainingSKUCategories = contextMetaInfo.remainingSKUCategories.filter((item) => item.label.toLowerCase() !== choosedOptionInfo.label.toLowerCase());
        brandArr = Object.keys(cafContext.sizeQuizInfo[choosedSKUCategory]);
        json = brandArr && brandArr.length && brandArr.map((item) => ({ label: item, key: item }));

        return {
          ruleExecutionContext: { value: json as SizeBrandObj[], choosedSKUCategory: CATEGORY_NAMES_MAPPING[choosedSKUCategory].toLowerCase() },
          updatedInfo: { remainingSKUCategories: remainingSKUCategories, choosedSKUCategory: choosedSKUCategory }
        };

      case RULE_NAMES_MAPPING.RULE_7:
        choosedSKUCategory = contextMetaInfo.choosedSKUCategory;
        choosedBrand = choosedOptionInfo.label;
        sizeArr = Object.keys(cafContext.sizeQuizInfo[choosedSKUCategory][choosedBrand].sizes);
        json = sizeArr && sizeArr.length && sizeArr.map((item) => ({ label: item.replace(/_/g, '-'), key: item }));

        return {
          ruleExecutionContext: { value: json as SizeBrandObj[], choosedSKUCategory: CATEGORY_NAMES_MAPPING[choosedSKUCategory].toLowerCase(), choosedBrand: choosedBrand },
          updatedInfo: { choosedBrand: choosedBrand }
        };

      case RULE_NAMES_MAPPING.RULE_8:
        remainingSKUCategories = contextMetaInfo.remainingSKUCategories;
        choosedSKUCategory = remainingSKUCategories[0].label;
        brandArr = Object.keys(cafContext.sizeQuizInfo[choosedSKUCategory]);
        json = brandArr && brandArr.length && brandArr.map((item) => ({ label: item, key: item }));
        choosedBrand = contextMetaInfo.choosedBrand;
        sizeInfo = cafContext.sizeQuizInfo[contextMetaInfo.choosedSKUCategory][choosedBrand].sizes[choosedOptionInfo.label];
        selectedInfo = {
          ...selectedInfo,
          [contextMetaInfo.choosedSKUCategory]: {
            anchorSizeBrand: {
              anchorBrand: choosedBrand,
              anchorSize: choosedOptionInfo.label,
              anchorSubVendor: sizeInfo['subVendor'],
              anchorType: sizeInfo['type'],
              productType: contextMetaInfo.choosedSKUCategory
            }
          }
        };

        globalContext.gb.utils.saveToStore(SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES, JSON.stringify(selectedInfo));

        return {
          ruleExecutionContext: { value: json as SizeBrandObj[], choosedSKUCategory: CATEGORY_NAMES_MAPPING[choosedSKUCategory].toLowerCase() },
          updatedInfo: { remainingSKUCategories: [], choosedSKUCategory: choosedSKUCategory, allSelectedInfo: selectedInfo }
        };

      case RULE_NAMES_MAPPING.RULE_9:
        choosedSKUCategory = contextMetaInfo.choosedSKUCategory;
        choosedBrand = choosedOptionInfo.label;
        sizeArr = Object.keys(cafContext.sizeQuizInfo[choosedSKUCategory][choosedBrand].sizes);
        json = sizeArr && sizeArr.length && sizeArr.map((item) => ({ label: item.replace(/_/g, '-'), key: item }));

        return {
          ruleExecutionContext: { value: json as SizeBrandObj[], choosedSKUCategory: CATEGORY_NAMES_MAPPING[choosedSKUCategory].toLowerCase(), choosedBrand: choosedBrand },
          updatedInfo: { choosedBrand: choosedBrand }
        };

      case RULE_NAMES_MAPPING.RULE_10:
        choosedSKUCategory = contextMetaInfo.choosedSKUCategory;
        choosedBrand = contextMetaInfo.choosedBrand;
        sizeInfo = cafContext.sizeQuizInfo[choosedSKUCategory][choosedBrand].sizes[choosedOptionInfo.label];
        selectedInfo = {
          ...selectedInfo,
          [choosedSKUCategory]: {
            anchorSizeBrand: {
              anchorBrand: choosedBrand,
              anchorSize: choosedOptionInfo.label,
              anchorSubVendor: sizeInfo['subVendor'],
              anchorType: sizeInfo['type'],
              productType: choosedSKUCategory
            }
          }
        };

        globalContext.gb.utils.saveToStore(
          SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES,
          JSON.stringify(selectedInfo)
        );

        return {
          ruleExecutionContext: {},
          updatedInfo: {}
        };

      case RULE_NAMES_MAPPING.RULE_11:
        if (!!globalContext.gb.customer.uuid) {
          // logged in user, save size-quiz info on appData
          globalContext.gb.utils.saveToStore('isSizeQuizNextClicked', true);
          handleSaveSizeQuizInfo();
        } else {
          // logged out user, open create account modal
          closeCAFModal(null);
          globalContext.gb.utils.saveToStore('isSizeQuizNextClicked', true);
          window.location.href = '/pages/size-quiz#create_account';
        }

        return null;

      default:
        return null;
    }
  };
  const appDataKeyForAnchorPreferences = SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES;
  /**
  * @ function getSizeQuizDataFromLocalStore
  * @ desc - fetching the anchor details obj from local store and parsing it
  * @ return - anchorObj {Object}
  */
  const getSizeQuizDataFromLocalStore = (userId: string): SizeQuizData => {
    let sizeQuizDataFromLocalStore;

    if (uuid) {
      // if user is loggedin get data from local store
      const localStoreKey = appDataKeyForAnchorPreferences + '_' + uuid;

      sizeQuizDataFromLocalStore = globalContext.gb.utils.getParsedDataFromStore(localStoreKey);
    }

    return sizeQuizDataFromLocalStore;
  };

  /**
   * @ function getSizeQuizDataFromStore
   * @ desc - fetching the anchor details obj from session store and parsing it
   * @ return - anchorObj {Object}
   */
  const getSizeQuizDataFromStore = (userId: string): SizeQuizData => {
    let sizeQuizDataFromStore;
    const sizeQuizDataFromSession = globalContext.gb.utils.getParsedDataFromStore(
      appDataKeyForAnchorPreferences
    );

    if (
      _.isObject(sizeQuizDataFromSession) &&
      !_.isEmpty(sizeQuizDataFromSession)
    ) {
      sizeQuizDataFromStore = sizeQuizDataFromSession;
    } else if (userId) {
      // if user is loggedin get data from local store
      sizeQuizDataFromStore = getSizeQuizDataFromLocalStore(userId);
    }
    return sizeQuizDataFromStore;
  }

  /**
  * @ function saveAnchorPrefDataInAppForSizeQuiz
  * @ param anchorDetailsObj - {Object} - anchor preferences from session store
  * @ desc process the anchor details obj from session store and
  * save the anchor data and status that user has seen size quiz in ups model
  */
  const saveAnchorPrefDataInAppForSizeQuiz = (
    anchorDetailsObj,
    userId: string
  ): void => {
    let savedSizeObj = {};

    if (anchorDetailsObj) {
      if (!isAnchorDetailsObjectInvalid(anchorDetailsObj)) {
        savedSizeObj = createSizeRecObject(anchorDetailsObj, savedSizeObj);
      }

      if (!_.isEmpty(savedSizeObj)) {
        updateSizeQuizStatusAndAncorPReferances(savedSizeObj, true).then((data) => {
          if (data) {
            globalContext.gb.utils.saveToStore(
              appDataKeyForAnchorPreferences + '_' + (userId || globalContext.gb.utils.getFromStore('uuid')),
              JSON.stringify(savedSizeObj),
              true
            );
            window.location.href = '/pages/subscription-plan'
          }
        });
      }
    }
  };



  const handleSaveSizeQuizInfo = (): void => {
    let anchorPrefObj;
    let prefObjFromSessionStore;

    if (window.performance && window.performance.mark) {
      window.performance.mark('mark_SQ_submit_btn_clicked');
    }

    // save anchor pref in ups
    prefObjFromSessionStore = getSizeQuizDataFromStore(
      appDataKeyForAnchorPreferences
    );

    if (
      globalContext.gb.utils.isPlainObject(prefObjFromSessionStore) &&
      !_.isEmpty(prefObjFromSessionStore)
    ) {
      anchorPrefObj = prefObjFromSessionStore;
    }

    anchorPrefObj = anchorPrefObj || {};

    globalContext.gb.utils.saveToStore('isSizeQuizNextClicked', true);

    // if user is logged-in and anchor pref obj is present so save it in ups
    if (isLoggedIn) {
      // save measurement info in ups
      saveAnchorPrefDataInAppForSizeQuiz(anchorPrefObj, uuid);
    }
  }
  const appendCoversationOnSession = ({ conversations }): void => {
    globalContext.gb.utils.saveToStore('caf_conv', JSON.stringify(conversations));
  };
  const handleOccassionCatgeoryClick = (options: OptionClickInfo): void => {
    let contextPool = { ruleExecutionContext: {}, updatedInfo: {} };
    let newConversation = fetchConversationFromAction({
      contextPool: contextPool,
      userIntent: RULE_NAMES_MAPPING.RULE_1
    });
    let allSelectedInfo = globalContext.gb.utils.getFromStore(
      SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES
    );

    try {
      allSelectedInfo = JSON.parse(
        globalContext.gb.utils.getFromStore(
          SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES
        )
      );
    } catch (error) {
      allSelectedInfo = {};
    }

    const contextMetaInfo = {
      choosedOccasionCategory: options.info.label,
      choosedBrand: '',
      choosedSKUCategory: '',
      choosedSize: '',
      remainingSKUCategories: createSKUCategories(cafContext.sizeQuizInfo)
    };

    newConversation.contextMetaInfo = contextMetaInfo;

    let updatedConversations = [newConversation];

    contextPool = getDispatchActionBasedContext({
      action: RULE_NAMES_MAPPING.RULE_2,
      choosedOptionInfo: options.info,
      contextMetaInfo: contextMetaInfo
    });
    newConversation = fetchConversationFromAction({
      userIntent: RULE_NAMES_MAPPING.RULE_2,
      contextPool: contextPool
    });
    newConversation.contextMetaInfo = {
      choosedOccasionCategory: options.info.label,
      choosedBrand: '',
      choosedSKUCategory: '',
      choosedSize: '',
      remainingSKUCategories: createSKUCategories(cafContext.sizeQuizInfo)
    };

    updatedConversations = [...updatedConversations, newConversation];
    appendCoversationOnSession({ conversations: updatedConversations });

    setCAFState((prevCAFState) => ({
      ...prevCAFState,
      allSelectedInfo: allSelectedInfo,
      conversations: updatedConversations,
      ...contextPool.updatedInfo,
      isModalOpen: true
    }));
  };
  const getPersistenceConversation = (): ConversationObj[] => {
    let conversations = [];

    try {
      conversations = JSON.parse(globalContext.gb.utils.getFromStore('caf_conv'));

      return conversations;
    } catch (error) {
      return conversations;
    }
  };
  const renderFirstConversation = (options: any): void => {
    const { dispatchAction } = options;
    const conversations = getPersistenceConversation();
    const contextPool = { ruleExecutionContext: {}, updatedInfo: {} };
    const newConversation = fetchConversationFromAction({ contextPool: contextPool, userIntent: dispatchAction });

    setCAFState((prevState) => {
      newConversation.contextMetaInfo = {
        choosedOccasionCategory: '',
        choosedBrand: '',
        choosedSKUCategory: '',
        choosedSize: '',
        remainingSKUCategories: [...prevState.remainingSKUCategories]
      };

      const updatedConversations = [...conversations, newConversation];

      appendCoversationOnSession({ conversations: updatedConversations });

      return ({
        ...prevState,
        isModalOpen: true,
        conversations: updatedConversations,
        choosedOccasionCategory: ''
      });
    });
  };
  const fireGAEvents = (eventDetails, selectedAnswer = null, location = null): void => {
    let choosedSKUCategory = null;

    if (cafState?.choosedSKUCategory) {
      choosedSKUCategory = CATEGORY_NAMES_MAPPING[cafState.choosedSKUCategory]?.toLowerCase() || cafState.choosedSKUCategory?.toLowerCase();
    }

    if (eventDetails?.label) {
      const formattedLabel = eventDetails?.label
        .replace('%selectedAnswer%', selectedAnswer)
        .replace('%location%', location)
        .replace('%selectedCategory%', choosedSKUCategory)
        .replace('%selectedBrand%', cafState?.choosedBrand);

      globalContext.gtm.pushVersionedEvent('2.x', eventDetails.category, eventDetails.action, formattedLabel);
    } else {
      globalContext.gtm.pushVersionedEvent('2.x', eventDetails.category, eventDetails.action);
    }
  };
  const openCAFModal = (): void => {
    setCAFState((prevCAFState) => ({ ...prevCAFState, isModalOpen: true }));
  };
  const closeCAFModal = (source): void => {
    const ContentElement = document.querySelector('.caf-modal__content');

    if (ContentElement) {
      ContentElement.classList.add('caf-modal__content--exit');
    }

    setTimeout(() => {
      if (ContentElement?.classList) {
        ContentElement.classList.remove('caf-modal__content--exit');
      }

      setCAFState((prevCAFState) => ({ ...prevCAFState, isModalOpen: false }));
    }, 300);

    if (source === null) {
      return;
    }
    const eventDetails = {
      category: 'Conversation',
      action: 'ClickDismissConversation',
      label: 'dismissed=%selectedAnswer%'
    };

    fireGAEvents(eventDetails, source);
  };
  const handleGetStartedClick = (eventInfo: any): void => {
    const { dispatchAction, eventAction } = eventInfo;
    const conversations = getPersistenceConversation();
    const eventDetails = { category: 'LandingPage', action: eventAction };

    fireGAEvents(eventDetails);

    if (conversations.length) {
      openCAFModal();
    } else {
      renderFirstConversation({ dispatchAction: dispatchAction });
    }
  };

  // opens default drawer, triggered for auto src
  const openDefaultDrawer = (eventInfo: any): void => {
    const conversations = getPersistenceConversation();
    if (conversations.length) {
      openCAFModal();
    } else {
      renderFirstConversation({ dispatchAction: eventInfo.dispatchAction });
    }
  }
  const handleInitializationTrigger = (eventInfo): void => {
    const {
      source, dispatchAction, selectedOccasionCategory, location
    } = eventInfo;

    if (source === CAF_INITIALIZATION_SRC.GET_STARTED_BTN) {
      handleGetStartedClick(eventInfo);

      return;
    }

    if (source === CAF_INITIALIZATION_SRC.AUTO) {
      openDefaultDrawer(eventInfo);
    }

    if (source === CAF_INITIALIZATION_SRC.OCCASSION_CATEGORY_BLOCK) {
      const eventDetails = {
        category: 'LandingPage',
        action: 'ClickLaunchQuestion',
        label: 'selectedAnswer=%selectedAnswer%, location=%location%'
      };

      fireGAEvents(eventDetails, selectedOccasionCategory?.toLowerCase(), location);
      handleOccassionCatgeoryClick({
        dispatchAction: dispatchAction,
        info: {
          label: selectedOccasionCategory,
          index: 0,
          value: selectedOccasionCategory
        }
      });
    }
  };

  useEffect(() => {
    if (cafContext.sizeQuizInfo && cafContext.eventInfo) {
      handleInitializationTrigger(cafContext.eventInfo);
    }
  }, [cafContext.eventInfo, cafContext.sizeQuizInfo]);

  // add conversation from session to context
  useEffect(() => {
    const persistentConversation = getPersistenceConversation();
    let contextMetaInfo = {};

    if (persistentConversation.length) {
      const lastConversation = persistentConversation[persistentConversation.length - 1];

      contextMetaInfo = lastConversation.contextMetaInfo || {};
    }

    setCAFState((_prevState) => ({
      ..._prevState,
      conversations: getPersistenceConversation(),
      ...contextMetaInfo
    }));
  }, []);
  useEffect(() => {
    if (cafContext.openModalForLoader && !cafContext.sizeQuizInfo) {
      openCAFModal();
    }
  }, [cafContext.openModalForLoader]);

  const optionClickHandler = ({ dispatchAction, info, eventDetails }: OptionClickInfo): void => {
    if (eventDetails) {
      fireGAEvents(eventDetails, info?.value);
    }

    let currentConversation = cafState.conversations[cafState.conversations.length - 1];
    let currentContextMetaInfo = currentConversation.contextMetaInfo || {};
    const dispatchActionOpts = { action: dispatchAction, choosedOptionInfo: info, contextMetaInfo: currentContextMetaInfo };
    const contextPool = getDispatchActionBasedContext(dispatchActionOpts);

    if (!contextPool) {
      return;
    }

    currentContextMetaInfo = { ...currentContextMetaInfo, [info?.key]: info?.label };
    currentConversation.contextMetaInfo = currentContextMetaInfo;

    const newConversation = fetchConversationFromAction({
      userIntent: dispatchAction,
      contextPool: contextPool
    });
    const newContextMetaInfo = { ...currentContextMetaInfo, ...contextPool.updatedInfo };

    setCAFState((prevState) => {
      newConversation.contextMetaInfo = newContextMetaInfo;

      const updatedConversations = [...prevState.conversations, newConversation];

      appendCoversationOnSession({ conversations: updatedConversations });

      return ({
        ...prevState,
        conversations: updatedConversations,
        ...newContextMetaInfo
      });
    });
  };
  const previousConvOptionClicked = (payload: OptionClickInfo): void => {
    if (payload?.eventDetails) {
      fireGAEvents(payload?.eventDetails, payload?.info?.value);
    }

    const { conversationId } = payload;
    const index = cafState.conversations.findIndex((item) => item.conversationId === conversationId);
    const currentConversation = Object.assign({}, cafState.conversations[index]);
    const requiredConversations = cafState.conversations.slice(0, index);
    let contextMetaInfo = { ...currentConversation.contextMetaInfo };
    const contextPool = getDispatchActionBasedContext({
      action: payload.dispatchAction,
      choosedOptionInfo: payload.info,
      contextMetaInfo: contextMetaInfo
    });
    let currentcontextMetaInfo = { ...contextMetaInfo, [payload?.info?.key]: payload?.info?.label };

    currentConversation.contextMetaInfo = currentcontextMetaInfo;

    const newConversation = fetchConversationFromAction({
      userIntent: payload.dispatchAction,
      contextPool: contextPool
    });
    const newContextMetaInfo = { ...currentcontextMetaInfo, ...contextPool.updatedInfo };

    newConversation.contextMetaInfo = newContextMetaInfo;

    const updatedConversations = [...requiredConversations, currentConversation, newConversation];
    // appendCoversationOnSession({ conversations: updatedConversations });

    setCAFState((prevState) => {
      appendCoversationOnSession({ conversations: updatedConversations });

      return ({
        ...prevState,
        conversations: updatedConversations,
        ...newContextMetaInfo
      });
    });
  };
  const scrollToBottom = (ref: React.MutableRefObject<HTMLDivElement | null>): void => {
    const lastChildElement = ref?.current?.lastElementChild;

    // TODO: look for cross browser compatibility ot this
    if (lastChildElement) {
      lastChildElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return {
    openCAFModal: openCAFModal,
    closeCAFModal: closeCAFModal,
    optionClickHandler: optionClickHandler,
    cafState: cafState,
    previousConvOptionClicked: previousConvOptionClicked,
    scrollToBottom: scrollToBottom
  };
};
